import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ApiDatabase from "server";
import UseContext from "hooks/useContext";
import useModal from "components/modal/useModal";
import Banner from "components/banner/banner";
import Button from "components/button";
import { Plus } from "assets/icons";
import PerimeterArchivedSection from "pages/profil/perimeters/PerimeterArchivedSection";
import PerimeterEmptyState from "pages/profil/perimeters/PerimeterEmptyState";
import PerimeterListSkeleton from "pages/profil/perimeters/PerimeterListSkeleton";
import PerimeterRow from "pages/profil/perimeters/PerimeterRow";
import {
    getPerimeterErrorMessage,
    isStalePerimeterError,
} from "pages/profil/perimeters/perimeterErrors";
import { fillText } from "pages/profil/perimeters/perimeterText";
import {
    getArchiveModalType,
    NO_PERIMETER_TARGET,
} from "pages/profil/perimeters/perimeterUsage";

const LOAD_ERROR_FORBIDDEN = "forbidden";
const LOAD_ERROR_FAILED = "failed";

const requestPerimeters = (status) =>
    new Promise((resolve, reject) => {
        ApiDatabase.getPerimeters({ status }, resolve, reject);
    });

const PerimetersContent = () => {
    const {
        companyData,
        language,
        refreshPerimeters,
        setRefreshPerimeters,
        setPerimeterModalData,
    } = UseContext();
    const { toggle } = useModal();
    const [perimeters, setPerimeters] = useState([]);
    const [archivedPerimeters, setArchivedPerimeters] = useState([]);
    const [assignableMembers, setAssignableMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [hasArchivedLoadError, setHasArchivedLoadError] = useState(false);
    const [checkingUsageId, setCheckingUsageId] = useState(null);
    const [restoringId, setRestoringId] = useState(null);
    const loadedCompanyIdRef = useRef(null);
    const isMountedRef = useRef(true);
    const companyId = companyData?._id ? String(companyData._id) : null;

    useEffect(() => {
        if (!companyId) return undefined;
        let cancelled = false;
        // A refresh keeps the current list on screen; only the first load, a
        // company switch or a retry after an error falls back to the skeleton.
        if (loadedCompanyIdRef.current !== companyId) {
            setPerimeters([]);
            setArchivedPerimeters([]);
            setAssignableMembers([]);
            setIsLoading(true);
        }
        setLoadError(null);

        // The archived list is secondary: its failure must not hide the active
        // perimeters, so it resolves to null instead of rejecting.
        const archivedRequest = requestPerimeters("archived").catch((err) => {
            console.error(err);
            return null;
        });

        Promise.all([requestPerimeters("active"), archivedRequest])
            .then(([active, archived]) => {
                if (cancelled) return;
                loadedCompanyIdRef.current = companyId;
                setPerimeters(Array.isArray(active?.data) ? active.data : []);
                setAssignableMembers(
                    Array.isArray(active?.assignableMembers)
                        ? active.assignableMembers
                        : []
                );
                setHasArchivedLoadError(archived === null);
                setArchivedPerimeters(
                    Array.isArray(archived?.data) ? archived.data : []
                );
                setIsLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error(err);
                loadedCompanyIdRef.current = null;
                setLoadError(
                    err?.response?.status === 403
                        ? LOAD_ERROR_FORBIDDEN
                        : LOAD_ERROR_FAILED
                );
                setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [companyId, refreshPerimeters]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleRetry = () => setRefreshPerimeters((count) => count + 1);

    const handleCreate = () => {
        setPerimeterModalData({ assignableMembers });
        toggle("perimeter_create");
    };

    const handleEdit = (perimeter) => {
        setPerimeterModalData({ perimeter, assignableMembers });
        toggle("perimeter_edit");
    };

    // The usage tells which modal fits (delete, archive or archive with a
    // transfer), so it is read before any modal opens.
    const handleArchive = (perimeter) => {
        if (checkingUsageId) return;
        setCheckingUsageId(perimeter.id);
        ApiDatabase.getPerimeterUsage(
            { id: perimeter.id, transferTo: NO_PERIMETER_TARGET },
            (res) => {
                if (!isMountedRef.current) return;
                setCheckingUsageId(null);
                const usage = res?.data;
                if (!usage) {
                    toast.error(language.getValue("perimeters.errors.generic"));
                    return;
                }
                setPerimeterModalData({ perimeter, usage, perimeters });
                toggle(getArchiveModalType(usage));
            },
            (err) => {
                console.error(err);
                if (!isMountedRef.current) return;
                setCheckingUsageId(null);
                toast.error(getPerimeterErrorMessage(language, err));
                if (isStalePerimeterError(err)) {
                    setRefreshPerimeters((count) => count + 1);
                }
            }
        );
    };

    const handleRestore = (perimeter) => {
        if (restoringId) return;
        // The finished missions come back restricted to a perimeter that has no
        // member yet, so the user confirms before restoring.
        if (Number(perimeter.usage?.finishedMissions) > 0) {
            setPerimeterModalData({ perimeter, usage: perimeter.usage });
            toggle("perimeter_restore");
            return;
        }
        setRestoringId(perimeter.id);
        ApiDatabase.postRestorePerimeter(
            { id: perimeter.id },
            () => {
                if (!isMountedRef.current) return;
                setRestoringId(null);
                toast.success(
                    fillText(language.getValue("perimeters.toast.restored"), {
                        name: perimeter.name,
                    })
                );
                setRefreshPerimeters((count) => count + 1);
            },
            (err) => {
                console.error(err);
                if (!isMountedRef.current) return;
                setRestoringId(null);
                toast.error(getPerimeterErrorMessage(language, err));
                if (isStalePerimeterError(err)) {
                    setRefreshPerimeters((count) => count + 1);
                }
            }
        );
    };

    const canCreate = !isLoading && !loadError;

    const renderContent = () => {
        if (isLoading) return <PerimeterListSkeleton />;
        if (loadError === LOAD_ERROR_FORBIDDEN) {
            return (
                <Banner type="alert" showIcon={false}>
                    {language.getValue("perimeters.list.forbidden")}
                </Banner>
            );
        }
        if (loadError) {
            return (
                <div className="flex flex-col items-start gap-3">
                    <Banner type="alert" showIcon={false}>
                        {language.getValue("perimeters.list.loadError")}
                    </Banner>
                    <Button color="SECONDARY" size="SM" onClick={handleRetry}>
                        {language.getValue("perimeters.list.retry")}
                    </Button>
                </div>
            );
        }
        return (
            <>
                {perimeters.length === 0 ? (
                    <PerimeterEmptyState />
                ) : (
                    <ul>
                        {perimeters.map((perimeter) => (
                            <PerimeterRow
                                key={perimeter.id}
                                perimeter={perimeter}
                                onEdit={handleEdit}
                                onArchive={handleArchive}
                                isArchiveDisabled={checkingUsageId !== null}
                            />
                        ))}
                    </ul>
                )}
                {hasArchivedLoadError ? (
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                        {language.getValue("perimeters.archived.loadError")}
                        <Button
                            color="SECONDARY"
                            size="SM"
                            onClick={handleRetry}
                        >
                            {language.getValue("perimeters.list.retry")}
                        </Button>
                    </div>
                ) : (
                    <PerimeterArchivedSection
                        perimeters={archivedPerimeters}
                        onRestore={handleRestore}
                        isRestoreDisabled={restoringId !== null}
                    />
                )}
            </>
        );
    };

    return (
        <div className="w-full px-2 tab:px-4 lg:px-8 py-8">
            <div className="flex max-w-3xl flex-col gap-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-xl font-medium text-gray-900">
                            {language.getValue("perimeters.title")}
                        </h2>
                        <p className="max-w-md text-sm text-gray-500">
                            {language.getValue("perimeters.description")}
                        </p>
                    </div>
                    <Button
                        color={canCreate ? "PRIMARY" : "DISABLED"}
                        size="SM"
                        disabled={!canCreate}
                        onClick={handleCreate}
                    >
                        <Plus
                            wh={12}
                            color={canCreate ? "#FFFFFF" : "#9CA3AF"}
                        />
                        {language.getValue("perimeters.list.create")}
                    </Button>
                </div>
                {renderContent()}
            </div>
        </div>
    );
};

export default PerimetersContent;
